<template>
  <div>
    <v-container fluid class="tc-edit-container">
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">{{ $t('Tax Consent') }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <div class="cfg-breadcrumb">
        <span>{{ $t('Tax Planning') }}</span>
        <v-icon size="14" class="cfg-breadcrumb-sep">mdi-chevron-right</v-icon>
        <router-link class="cfg-breadcrumb-link" to="/TaxConsent">{{ $t('Tax Consent') }}</router-link>
        <v-icon size="14" class="cfg-breadcrumb-sep">mdi-chevron-right</v-icon>
        <span class="cfg-breadcrumb-current">{{ versionCode }}</span>
      </div>

      <!-- Header card -->
      <v-card class="card2">
        <v-card-text>
          <div class="d-flex align-center flex-wrap ga-2">
            <div class="text-subtitle-1 font-weight-medium d-flex align-center">
              <v-icon class="mr-1">mdi-shield-check-outline</v-icon>
              Consent {{ versionCode }}
            </div>
            <v-chip :color="meta.isActive ? 'success' : 'grey'" variant="tonal" size="small">
              {{ meta.isActive ? 'Active' : 'Inactive' }}
            </v-chip>
          </div>
          <p class="text-caption text-grey mt-1 mb-0">{{ meta.description || '-' }}</p>
          <p v-if="meta.updateDate" class="text-caption text-grey mb-0">
            <v-icon size="13" class="mr-1">mdi-history</v-icon>
            Last saved: {{ formatDateTime(meta.updateDate) }}
          </p>
          <v-alert v-if="meta.isActive" type="warning" variant="tonal" density="compact" icon="mdi-broadcast" class="mt-3 mb-0 tc-live-alert">
            {{ $t('taxConsentLiveBanner') }}
          </v-alert>
        </v-card-text>
      </v-card>

      <v-form ref="form" v-model="formValid">
        <!-- Card 1 — Consent Dialog Content (free-text HTML) -->
        <v-card class="card2">
          <v-card-title class="card-section-title">
            <v-icon class="mr-1">mdi-message-text-outline</v-icon>
            {{ $t('Consent Dialog Content') }}
          </v-card-title>
          <p class="card-section-subtitle">{{ $t('taxConsentDialogSubtitle') }}</p>

          <v-card-text>
            <v-text-field
              v-model="config.dialog.version"
              label="Consent version key"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-key-variant"
              :rules="[rules.required, rules.max50]"
              hint="ใช้ผูกกับ session — เปลี่ยนค่านี้จะถามความยินยอมใหม่"
              persistent-hint
            />

            <!-- Avatar / icon image (not language-specific) shown above the title. Upload
                 returns the public URL; SAVE persists it, as in Tax Planning mobile config. -->
            <div class="text-subtitle-2 mt-4 mb-2">{{ $t('taxConsentIconLabel') }}</div>
            <div class="d-flex align-center flex-wrap ga-3">
              <v-avatar size="64" color="grey-lighten-3" class="tc-icon-thumb">
                <v-img v-if="safeIconUrl" :src="safeIconUrl" :alt="$t('taxConsentIconLabel')" cover />
                <v-icon v-else size="32" color="grey">mdi-account-circle-outline</v-icon>
              </v-avatar>
              <v-btn variant="outlined" color="primary" rounded="lg" prepend-icon="mdi-upload" :loading="iconUploading" @click="triggerIconUpload">
                {{ $t('taxConsentUploadIcon') }}
              </v-btn>
              <v-btn v-if="config.dialog.iconUrl" variant="text" color="error" :disabled="iconUploading" @click="config.dialog.iconUrl = ''">
                {{ $t('taxConsentRemoveIcon') }}
              </v-btn>
              <span class="text-caption text-medium-emphasis">{{ $t('taxConsentIconHint') }}</span>
            </div>
            <div v-if="config.dialog.iconUrl" class="text-caption text-medium-emphasis text-truncate mt-1" :title="config.dialog.iconUrl">
              {{ config.dialog.iconUrl }}
            </div>
            <input ref="iconInput" type="file" accept="image/*" style="display: none" :aria-label="$t('taxConsentUploadIcon')" @change="handleIconUpload" />

            <v-tabs v-model="langTab" color="primary" density="comfortable" class="mt-3 tc-lang-tabs">
              <v-tab v-for="lang in langs" :key="lang.key" :value="lang.key">
                {{ lang.label }}
                <v-icon
                  v-if="langStatus(lang.key) !== 'complete'"
                  size="13"
                  class="ml-1"
                  :class="langStatus(lang.key) === 'partial' ? 'tc-dot-partial' : 'tc-dot-empty'"
                  :icon="langStatus(lang.key) === 'partial' ? 'mdi-alert-circle' : 'mdi-circle-outline'"
                  :aria-label="langStatusLabel(lang.key)"
                  :title="langStatusLabel(lang.key)"
                />
              </v-tab>
            </v-tabs>
            <v-divider />

            <div class="mt-4">
              <v-text-field
                v-model="config.dialog.title[langTab]"
                :label="`${$t('taxConsentTitleLabel')} (${activeLang.label})`"
                variant="outlined"
                density="comfortable"
                class="mt-3"
                prepend-inner-icon="mdi-format-title"
                :rules="langTab === 'th' ? [rules.required] : []"
              />

              <v-row dense class="mt-1">
                <v-col cols="12" md="6">
                  <v-text-field v-model="config.dialog.acceptLabel[langTab]" label="Accept button label" variant="outlined" density="comfortable" prepend-inner-icon="mdi-check" hide-details />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="config.dialog.rejectLabel[langTab]" label="Reject button label" variant="outlined" density="comfortable" prepend-inner-icon="mdi-close" hide-details />
                </v-col>
              </v-row>

              <!-- Revoke link shown in place of the buttons once consent has been given -->
              <v-row dense class="mt-1 align-center">
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="config.dialog.cancelConsentWording[langTab]"
                    :label="`${$t('taxConsentCancelWordingLabel')} (${activeLang.label})`"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-link-variant-off"
                    :placeholder="$t('taxConsentCancelWordingPlaceholder')"
                    persistent-placeholder
                    hide-details
                  />
                </v-col>
                <v-col cols="8" md="4">
                  <v-text-field
                    v-model="config.dialog.cancelConsentWordingColor"
                    :label="$t('taxConsentCancelColorLabel')"
                    variant="outlined"
                    density="comfortable"
                    placeholder="#B3261E"
                    persistent-placeholder
                    hide-details
                  >
                    <!-- Swatch doubles as the native picker; it always shows the colour
                         actually applied, so typing an invalid hex is visibly corrected. -->
                    <template #prepend-inner>
                      <div class="tc-color-picker-wrapper tc-cancel-color-swatch" :style="{ backgroundColor: safeCancelColor }">
                        <input
                          type="color"
                          :value="safeCancelColor"
                          @change="config.dialog.cancelConsentWordingColor = $event.target.value"
                          class="tc-color-picker"
                          :aria-label="$t('taxConsentCancelColorLabel')"
                        />
                        <v-tooltip activator="parent" location="top">{{ $t('taxConsentCancelColorLabel') }}</v-tooltip>
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="4" md="3" class="d-flex align-center">
                  <v-switch
                    v-model="config.dialog.cancelConsentUnderline"
                    :label="$t('taxConsentCancelUnderlineLabel')"
                    density="compact"
                    color="primary"
                    hide-details
                  />
                </v-col>
              </v-row>

              <!-- Free-text HTML editor + live preview -->
              <div class="ml-group">
                <div class="d-flex align-center justify-space-between mb-2 flex-wrap ga-2">
                  <div class="ml-group-label mb-0">
                    {{ $t('taxConsentContentLabel') }}
                    <span class="text-caption text-grey">({{ activeLang.label }})</span>
                  </div>
                  <div class="tc-toolbar">
                    <v-btn v-for="tool in htmlTools" :key="tool.label" size="x-small" variant="text" class="tc-tool-btn" :aria-label="tool.label" @click="insertTag(tool.open, tool.close)">
                      <v-icon size="16">{{ tool.icon }}</v-icon>
                      <v-tooltip activator="parent" location="top">{{ tool.label }}</v-tooltip>
                    </v-btn>
                    <div class="tc-color-picker-wrapper" :style="{ backgroundColor: pickerColor }">
                      <input type="color" :value="pickerColor" @change="pickerColor = $event.target.value" class="tc-color-picker" />
                      <v-tooltip activator="parent" location="top">Pick color</v-tooltip>
                    </div>
                    <v-btn size="x-small" variant="text" class="tc-tool-btn tc-apply-color-btn" aria-label="Apply color" @click="insertColorTag(pickerColor)">
                      <span class="tc-apply-color-letter">A</span>
                      <v-tooltip activator="parent" location="top">Apply color</v-tooltip>
                    </v-btn>
                  </div>
                </div>

                <v-row dense class="">
                  <v-col cols="12" md="6" class="ga-4 d-flex flex-column">
                    <div class="tc-source-label"><v-icon size="14" class="mr-1">mdi-code-tags</v-icon>HTML</div>
                    <textarea
                      ref="htmlEditor"
                      v-model="config.dialog.contentHtml[langTab]"
                      class="tc-html-source"
                      spellcheck="false"
                      rows="14"
                      :placeholder="htmlPlaceholder"
                      :aria-label="`Consent content HTML (${activeLang.label})`"
                      @focus="activeHtmlEditor = 'main'"
                    ></textarea>

                    <div class="tc-source-label"><v-icon size="14" class="mr-1">mdi-code-tags</v-icon>HTML (Scrollable / Read More)</div>
                    <textarea
                      ref="htmlScrollableEditor"
                      v-model="config.dialog.contentHtmlScrollable[langTab]"
                      class="tc-html-source"
                      spellcheck="false"
                      rows="14"
                      :placeholder="htmlPlaceholder"
                      :aria-label="`Consent scrollable content HTML (${activeLang.label})`"
                      @focus="activeHtmlEditor = 'scrollable'"
                    ></textarea>
                    <p class="tc-source-hint">{{ $t('taxConsentContentHint') }}</p>
                  </v-col>

                  <v-col cols="12" md="6" class="d-flex flex-column justify-space-between">
                    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
                      <div class="tc-source-label"><v-icon size="14" class="mr-1">mdi-eye-outline</v-icon>{{ $t('taxConsentPreviewLabel') }}</div>
                      <v-switch
                        v-model="previewCancel"
                        :label="$t('taxConsentPreviewCancelLabel')"
                        density="compact"
                        color="primary"
                        hide-details
                      />
                    </div>
                    <div class="tc-phone" :style="{ backgroundImage: `url(${consentBg})` }" aria-hidden="true">
                      <!-- Status bar -->
                      <div class="tc-phone-statusbar">
                        <span class="tc-phone-time">9:41</span>
                        <span class="tc-phone-signal">
                          <v-icon size="13">mdi-signal</v-icon>
                          <v-icon size="13">mdi-wifi</v-icon>
                          <v-icon size="17">mdi-battery</v-icon>
                        </span>
                      </div>

                      <!-- Top bar: back button + fixed header title -->
                      <div class="tc-phone-topbar">
                        <span class="tc-phone-back"><v-icon size="20">mdi-chevron-left</v-icon></span>
                        <span class="tc-phone-header-title">Genie Tax Planner</span>
                      </div>

                      <!-- Frosted consent sheet -->
                      <div class="tc-phone-sheet">
                        <div class="tc-phone-scroll ga-2">
                          <div class="tc-phone-avatar">
                            <img v-if="safeIconUrl" :src="safeIconUrl" class="tc-phone-avatar-img" alt="" />
                            <v-icon v-else size="42" color="grey-lighten-1">mdi-robot-happy-outline</v-icon>
                          </div>
                          <div class="tc-phone-title">
                            {{ config.dialog.title[langTab] || $t('taxConsentNoTitle') }}
                          </div>
                          <div v-if="previewHtml" class="tc-phone-body tc-preview-body d-flex flex-column flex-0-1" v-html="previewHtml"></div>
                          <div v-if="previewScrollableHtml" class="tc-phone-body tc-preview-body tc-preview-scrollable scroll-area" v-html="previewScrollableHtml"></div>

                          <div v-if="!previewHtml && !previewScrollableHtml" class="tc-phone-body tc-preview-empty">
                            {{ $t('taxConsentPreviewEmpty') }}
                          </div>
                        </div>

                        <!-- Action buttons, or the revoke link in cancel-consent mode -->
                        <div v-if="!previewCancel" class="tc-phone-actions">
                          <span class="tc-phone-btn ghost">{{ config.dialog.rejectLabel[langTab] || 'Decline' }}</span>
                          <span class="tc-phone-btn primary">{{ config.dialog.acceptLabel[langTab] || 'Accept' }}</span>
                        </div>
                        <div v-else class="tc-phone-actions tc-phone-actions-cancel">
                          <span class="tc-phone-cancel-link" :style="cancelLinkStyle">
                            {{ config.dialog.cancelConsentWording[langTab] || $t('taxConsentCancelWordingPlaceholder') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p class="tc-preview-approx"><v-icon size="13" class="mr-1">mdi-information-outline</v-icon>{{ $t('taxConsentPreviewApprox') }}</p>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-form>

      <!-- Card 3 — Audit & Logging -->
      <v-card class="card2">
        <v-card-title class="card-section-title">
          <v-icon class="mr-1">mdi-clipboard-text-clock-outline</v-icon>
          {{ $t('Audit & Logging') }}
        </v-card-title>
        <p class="card-section-subtitle">{{ $t('taxConsentAuditSubtitle') }}</p>

        <v-card-text>
          <v-data-table :headers="logHeaders" :items="logs" :loading="logsLoading" class="elevation-1" density="comfortable" :items-per-page="10">
            <template v-slot:item.createdAt="{ item }">
              {{ formatDateTime(item.createdAt) }}
            </template>
            <template v-slot:item.logType="{ item }">
              <v-chip :color="logTypeColor(item.logType)" variant="tonal" size="small">
                {{ item.logType }}
              </v-chip>
            </template>
            <template v-slot:no-data>
              <div class="text-center text-grey pa-4">No logs yet</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Sticky action bar -->
    <div class="tc-action-bar">
      <v-container fluid class="d-flex align-center py-0">
        <v-btn variant="outlined" color="primary" rounded="lg" prepend-icon="mdi-arrow-left" @click="goBack"> Back </v-btn>
        <v-spacer />
        <v-chip v-if="isDirty" color="warning" variant="tonal" size="small" class="mr-3">
          <v-icon start size="14">mdi-pencil</v-icon>
          Unsaved changes
        </v-chip>
        <v-btn color="primary" variant="flat" rounded="lg" prepend-icon="mdi-content-save" :loading="saving" @click="save"> SAVE </v-btn>
      </v-container>
    </div>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" timeout="4500">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import taxConsentBg from '@/assets/lhb/tax-consent-bg.jpg';

export default {
  name: 'TaxConsentEdit',
  data() {
    return {
      flagNav: true,
      consentBg: taxConsentBg,
      today: new Date().toLocaleDateString(),
      loading: false,
      saving: false,
      iconUploading: false,
      logsLoading: false,
      formValid: true,
      langTab: 'th',
      // Flips the phone mock between the consent card and the cancel-consent card.
      previewCancel: false,
      langs: [
        { key: 'th', label: 'ไทย (TH)' },
        { key: 'en', label: 'English (EN)' },
        { key: 'zh', label: '中文 (ZH)' },
      ],
      htmlTools: [
        { label: 'Paragraph', icon: 'mdi-format-paragraph', open: '<p>', close: '</p>' },
        { label: 'Heading', icon: 'mdi-format-header-4', open: '<h4>', close: '</h4>' },
        { label: 'Bold', icon: 'mdi-format-bold', open: '<strong>', close: '</strong>' },
        { label: 'Italic', icon: 'mdi-format-italic', open: '<em>', close: '</em>' },
        { label: 'Bullet list', icon: 'mdi-format-list-bulleted', open: '<ul>\n  <li>', close: '</li>\n</ul>' },
        { label: 'Numbered list', icon: 'mdi-format-list-numbered', open: '<ol>\n  <li>', close: '</li>\n</ol>' },
        { label: 'Line break', icon: 'mdi-keyboard-return', open: '<br>\n', close: '' },
        { label: 'Center', icon: 'mdi-format-horizontal-align-center', open: '<center>', close: '</center>' },
      ],
      meta: this.getEmptyMeta(),
      config: this.getDefaultConfig(),
      pickerColor: '#1976d2',
      activeHtmlEditor: 'main',
      baseline: '',
      logs: [],
      logHeaders: [
        { title: 'Time', key: 'createdAt', sortable: false, width: '180px' },
        { title: 'Type', key: 'logType', sortable: false, width: '150px' },
        { title: 'Actor', key: 'actor', sortable: false, width: '120px' },
        { title: 'Action', key: 'action', sortable: false, width: '220px' },
        { title: 'Details', key: 'details', sortable: false },
      ],
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      rules: {
        required: (value) => (value !== null && value !== undefined && String(value).trim() !== '') || 'Required',
        max50: (value) => String(value || '').length <= 50 || 'Maximum 50 characters',
        nonNegative: (value) => Number(value) >= 0 || 'Must be 0 or greater',
      },
    };
  },
  computed: {
    versionCode() {
      return String(this.$route.params.versionCode || '').toUpperCase();
    },
    activeLang() {
      return this.langs.find((l) => l.key === this.langTab) || this.langs[0];
    },
    htmlPlaceholder() {
      return '<p>...</p>\n<ul>\n  <li>...</li>\n</ul>';
    },
    // Sanitized render of the active language's HTML for the live preview.
    previewHtml() {
      const raw = this.config.dialog.contentHtml[this.langTab] || '';
      if (!String(raw).trim()) return '';
      return this.sanitizeHtml(raw);
    },
    // Sanitized render of the active language's scrollable HTML for the live preview.
    previewScrollableHtml() {
      const raw = this.config.dialog.contentHtmlScrollable[this.langTab] || '';
      if (!String(raw).trim()) return '';
      return this.sanitizeHtml(raw);
    },
    // Only render the icon in the preview when the URL is a safe image source
    // (http/https or a data:image URI) — guards the <img> against javascript: etc.
    safeIconUrl() {
      const raw = String(this.config.dialog.iconUrl || '').trim();
      if (!raw) return '';
      return /^(https?:\/\/|data:image\/)/i.test(raw) ? raw : '';
    },
    // Validate the author-supplied colour before it reaches a style binding —
    // same guard philosophy as safeIconUrl above.
    safeCancelColor() {
      const raw = String(this.config.dialog.cancelConsentWordingColor || '').trim();
      return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw) ? raw : '#B3261E';
    },
    cancelLinkStyle() {
      return {
        color: this.safeCancelColor,
        textDecoration: this.config.dialog.cancelConsentUnderline ? 'underline' : 'none',
      };
    },
    isDirty() {
      return this.baseline !== '' && this.snapshot() !== this.baseline;
    },
  },
  mounted() {
    this.loadVersion();
    this.loadLogs();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !window.confirm('มีการแก้ไขที่ยังไม่ได้บันทึก ต้องการออกจากหน้านี้ใช่หรือไม่?')) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    getEmptyMeta() {
      return {
        consentVersionID: 0,
        versionCode: '',
        description: '',
        effectiveStartDate: null,
        effectiveEndDate: null,
        isActive: false,
        createDate: null,
        updateDate: null,
      };
    },

    getDefaultConfig() {
      return {
        dialog: {
          version: '',
          title: { th: '', en: '', zh: '' },
          subtitle: { th: '', en: '', zh: '' },
          iconUrl: '',
          acceptLabel: { th: '', en: '', zh: '' },
          rejectLabel: { th: '', en: '', zh: '' },
          contentHtml: { th: '', en: '', zh: '' },
          contentHtmlScrollable: { th: '', en: '', zh: '' },
          cancelConsentWording: { th: '', en: '', zh: '' },
          cancelConsentWordingColor: '#B3261E',
          cancelConsentUnderline: true,
          updatedAt: '',
        },
      };
    },

    emptyLocalized() {
      return { th: '', en: '', zh: '' };
    },

    // Deep clone helper (defensive against missing structuredClone)
    deepClone(value) {
      if (value === null || value === undefined) return value;
      try {
        if (typeof structuredClone === 'function') return structuredClone(value);
      } catch (e) {
        // fall through to JSON clone
      }
      return JSON.parse(JSON.stringify(value));
    },

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    // Single shared sanitizer for both the live preview and the persisted value,
    // so what the author sees in the preview is exactly what gets stored and
    // handed to the external customer app. Forces rel="noopener noreferrer" on
    // any link that opens a new tab.
    sanitizeHtml(raw) {
      const addRel = (node) => {
        if (node.tagName === 'A' && node.getAttribute && node.getAttribute('target') === '_blank') {
          node.setAttribute('rel', 'noopener noreferrer');
        }
      };
      DOMPurify.addHook('afterSanitizeAttributes', addRel);
      try {
        return DOMPurify.sanitize(String(raw || ''), { ADD_ATTR: ['target', 'rel'] });
      } finally {
        DOMPurify.removeHook('afterSanitizeAttributes');
      }
    },

    // Build an HTML body from the legacy structured fields (description / bullets /
    // read-more sections) so versions created before the free-text redesign still
    // show their content. Used only when contentHtml is empty for a language.
    legacyToHtml(dialog, lang) {
      const parts = [];
      const desc = dialog && dialog.description ? dialog.description[lang] : '';
      if (desc && String(desc).trim()) parts.push(`<p>${this.escapeHtml(desc)}</p>`);

      const bullets = Array.isArray(dialog && dialog.bullets) ? dialog.bullets : [];
      const items = bullets
        .map((b) => (b ? b[lang] : ''))
        .filter((t) => t && String(t).trim())
        .map((t) => `  <li>${this.escapeHtml(t)}</li>`);
      if (items.length) parts.push(`<ul>\n${items.join('\n')}\n</ul>`);

      const sections = Array.isArray(dialog && dialog.readMoreSections) ? dialog.readMoreSections : [];
      sections.forEach((s) => {
        const title = s && s.title ? s.title[lang] : '';
        const body = s && s.body ? s.body[lang] : '';
        if (title && String(title).trim()) parts.push(`<h4>${this.escapeHtml(title)}</h4>`);
        if (body && String(body).trim()) parts.push(`<p>${this.escapeHtml(body)}</p>`);
      });

      return parts.join('\n');
    },

    // Build scrollable HTML body from legacy read-more sections.
    legacyScrollableToHtml(dialog, lang) {
      const parts = [];
      const sections = Array.isArray(dialog && dialog.readMoreSections) ? dialog.readMoreSections : [];
      sections.forEach((s) => {
        const title = s && s.title ? s.title[lang] : '';
        const body = s && s.body ? s.body[lang] : '';
        if (title && String(title).trim()) parts.push(`<h4>${this.escapeHtml(title)}</h4>`);
        if (body && String(body).trim()) parts.push(`<p>${this.escapeHtml(body)}</p>`);
      });
      return parts.join('\n');
    },

    // Stable snapshot of the editable config, used for dirty tracking.
    snapshot() {
      return JSON.stringify(this.config);
    },

    // Merge a loaded config onto the default shape so the form never throws.
    normalizeConfig(raw) {
      const base = this.getDefaultConfig();
      const src = raw && typeof raw === 'object' ? this.deepClone(raw) : {};

      const dialog = src.dialog && typeof src.dialog === 'object' ? src.dialog : {};
      const rawHtml = dialog.contentHtml && typeof dialog.contentHtml === 'object' ? dialog.contentHtml : {};
      const rawScrollableHtml = dialog.contentHtmlScrollable && typeof dialog.contentHtmlScrollable === 'object' ? dialog.contentHtmlScrollable : {};

      const resolveHtml = (lang) => {
        const existing = rawHtml[lang];
        if (existing && String(existing).trim()) return existing;
        return this.legacyToHtml(dialog, lang);
      };

      const resolveScrollableHtml = (lang) => {
        const existing = rawScrollableHtml[lang];
        if (existing && String(existing).trim()) return existing;
        return this.legacyScrollableToHtml(dialog, lang);
      };

      return {
        dialog: {
          version: dialog.version || '',
          title: { ...base.dialog.title, ...(dialog.title || {}) },
          subtitle: { ...base.dialog.subtitle, ...(dialog.subtitle || {}) },
          iconUrl: dialog.iconUrl || '',
          acceptLabel: { ...base.dialog.acceptLabel, ...(dialog.acceptLabel || {}) },
          rejectLabel: { ...base.dialog.rejectLabel, ...(dialog.rejectLabel || {}) },
          contentHtml: {
            th: resolveHtml('th'),
            en: resolveHtml('en'),
            zh: resolveHtml('zh'),
          },
          contentHtmlScrollable: {
            th: resolveScrollableHtml('th'),
            en: resolveScrollableHtml('en'),
            zh: resolveScrollableHtml('zh'),
          },
          cancelConsentWording: { ...base.dialog.cancelConsentWording, ...(dialog.cancelConsentWording || {}) },
          cancelConsentWordingColor: dialog.cancelConsentWordingColor || base.dialog.cancelConsentWordingColor,
          // Checked explicitly rather than with `||` so a stored `false` survives the merge.
          cancelConsentUnderline:
            typeof dialog.cancelConsentUnderline === 'boolean'
              ? dialog.cancelConsentUnderline
              : base.dialog.cancelConsentUnderline,
          updatedAt: dialog.updatedAt || '',
        },
      };
    },

    // Per-language completeness for the tab indicator and save validation.
    // 'complete' = title + body present, 'empty' = neither, 'partial' = one missing.
    langStatus(lang) {
      const hasTitle = !!String(this.config.dialog.title[lang] || '').trim();
      const hasBody = !!String(this.config.dialog.contentHtml[lang] || '').trim();
      if (hasTitle && hasBody) return 'complete';
      if (!hasTitle && !hasBody) return 'empty';
      return 'partial';
    },

    langStatusLabel(lang) {
      return this.langStatus(lang) === 'partial' ? 'ยังกรอกไม่ครบ — ต้องมีทั้งหัวข้อและเนื้อหา' : 'ยังไม่มีเนื้อหาในภาษานี้';
    },

    extractErrorMessage(error, fallback) {
      return error?.response?.data?.message || error?.response?.data?.Message || fallback;
    },

    callApi(methodName, ...args) {
      return new Promise((resolve, reject) => {
        if (!this.$call || typeof this.$call[methodName] !== 'function') {
          reject(new Error(`${methodName} is not available`));
          return;
        }
        this.$call[methodName](...args, resolve, reject);
      });
    },

    triggerIconUpload() {
      this.$refs.iconInput.value = '';
      this.$refs.iconInput.click();
    },

    async handleIconUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.showError(this.$t('taxConsentIconNotImage'));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.showError(this.$t('taxConsentIconTooLarge'));
        return;
      }

      const formData = new FormData();
      formData.append('entity', file, file.name);

      this.iconUploading = true;
      try {
        const response = await this.callApi('UploadTaxConsentIcon', {
          entity: formData,
          USER_CHANNEL: this.$root.User_Id || 'tax_consent',
        });
        if (response && response.status === 0 && response.data) {
          this.config.dialog.iconUrl = response.data;
          this.showSuccess(this.$t('taxConsentIconUploaded'));
        } else {
          this.showError((response && response.message) || 'Upload failed');
        }
      } catch (error) {
        console.error('Error uploading consent icon:', error);
        this.showError(this.extractErrorMessage(error, 'Upload failed'));
      } finally {
        this.iconUploading = false;
      }
    },

    async validateForm() {
      const result = await this.$refs.form?.validate();
      return typeof result === 'object' ? result.valid : result;
    },

    async loadVersion() {
      this.loading = true;
      try {
        const response = await this.callApi('GetTaxConsentByCode', this.versionCode);
        if (response.status === 0 && response.data) {
          const data = response.data;
          this.meta = {
            consentVersionID: data.consentVersionID,
            versionCode: data.versionCode,
            description: data.description || '',
            effectiveStartDate: data.effectiveStartDate || null,
            effectiveEndDate: data.effectiveEndDate || null,
            isActive: !!data.isActive,
            createDate: data.createDate || null,
            updateDate: data.updateDate || null,
          };
          this.config = this.normalizeConfig(data.config);
          this.$nextTick(() => {
            this.baseline = this.snapshot();
          });
        } else {
          this.showError(response.message || `Consent ${this.versionCode} not found`);
          this.$router.replace('/TaxConsent');
        }
      } catch (error) {
        console.error('Error loading consent version:', error);
        this.showError(this.extractErrorMessage(error, `Consent ${this.versionCode} not found`));
        this.$router.replace('/TaxConsent');
      } finally {
        this.loading = false;
      }
    },

    async loadLogs() {
      this.logsLoading = true;
      try {
        const response = await this.callApi('GetTaxConsentLogs');
        this.logs = response.status === 0 ? response.data || [] : [];
      } catch (error) {
        console.error('Error loading consent logs:', error);
        this.logs = [];
      } finally {
        this.logsLoading = false;
      }
    },

    // Insert / wrap an HTML tag pair at the caret of the active source editor.
    insertTag(open, close = '') {
      const lang = this.langTab;
      const isScrollable = this.activeHtmlEditor === 'scrollable';
      const editorRef = isScrollable ? this.$refs.htmlScrollableEditor : this.$refs.htmlEditor;
      const target = isScrollable ? 'contentHtmlScrollable' : 'contentHtml';
      const current = String(this.config.dialog[target][lang] || '');

      if (!editorRef) {
        this.config.dialog[target][lang] = current + open + close;
        return;
      }

      const start = typeof editorRef.selectionStart === 'number' ? editorRef.selectionStart : current.length;
      const end = typeof editorRef.selectionEnd === 'number' ? editorRef.selectionEnd : current.length;
      const selected = current.slice(start, end);
      const next = current.slice(0, start) + open + selected + close + current.slice(end);
      this.config.dialog[target][lang] = next;

      this.$nextTick(() => {
        editorRef.focus();
        const caret = selected ? start + open.length + selected.length + close.length : start + open.length;
        try {
          editorRef.setSelectionRange(caret, caret);
        } catch (e) {
          /* ignore */
        }
      });
    },

    insertColorTag(color) {
      this.pickerColor = color;
      this.insertTag(`<span style="color: ${color}">`, '</span>');
    },

    formatDateTime(value) {
      if (!value) return '-';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString();
    },

    logTypeColor(type) {
      switch (type) {
        case 'consent':
          return 'success';
        case 'data_access':
          return 'info';
        case 'processing':
          return 'primary';
        default:
          return 'grey';
      }
    },

    // Build the config to persist: free-text HTML is the source of truth for the
    // dialog body; legacy structured fields are intentionally dropped.
    buildConfigForSave() {
      const cfg = this.deepClone(this.config);
      // Sanitize at the moment of persistence — the stored value is what the
      // external customer app renders, so it must be clean, not just the
      // preview. DOMPurify keeps every tag the toolbar produces.
      cfg.dialog.contentHtml = {
        th: this.sanitizeHtml(String(cfg.dialog.contentHtml.th || '').trim()),
        en: this.sanitizeHtml(String(cfg.dialog.contentHtml.en || '').trim()),
        zh: this.sanitizeHtml(String(cfg.dialog.contentHtml.zh || '').trim()),
      };
      cfg.dialog.contentHtmlScrollable = {
        th: this.sanitizeHtml(String(cfg.dialog.contentHtmlScrollable.th || '').trim()),
        en: this.sanitizeHtml(String(cfg.dialog.contentHtmlScrollable.en || '').trim()),
        zh: this.sanitizeHtml(String(cfg.dialog.contentHtmlScrollable.zh || '').trim()),
      };
      // The revoke link is plain text, not HTML, so it needs no DOMPurify pass — the
      // same treatment acceptLabel/rejectLabel get. The colour is normalized here
      // because nothing sanitizes this config server-side.
      cfg.dialog.cancelConsentWording = {
        th: String(cfg.dialog.cancelConsentWording.th || '').trim(),
        en: String(cfg.dialog.cancelConsentWording.en || '').trim(),
        zh: String(cfg.dialog.cancelConsentWording.zh || '').trim(),
      };
      cfg.dialog.cancelConsentWordingColor = this.safeCancelColor;
      return cfg;
    },

    async save() {
      // Language-aware required check: jump to the offending tab so the error is
      // never hidden behind an inactive language tab.
      if (!String(this.config.dialog.title.th || '').trim()) {
        this.langTab = 'th';
        await this.$nextTick();
        await this.validateForm();
        this.showError('กรุณากรอกหัวข้อ (ไทย) ก่อนบันทึก');
        return;
      }
      if (!String(this.config.dialog.contentHtml.th || '').trim()) {
        this.langTab = 'th';
        this.showError('กรุณากรอกเนื้อหา consent (ไทย) ก่อนบันทึก');
        return;
      }

      // Per-language completeness: EN/ZH must have BOTH a title and body, or
      // neither — never half-filled (which would otherwise ship silently).
      const partial = this.langs.find((l) => this.langStatus(l.key) === 'partial');
      if (partial) {
        this.langTab = partial.key;
        this.showError(`ภาษา ${partial.label}: กรุณากรอกทั้งหัวข้อและเนื้อหา หรือเว้นว่างทั้งคู่`);
        return;
      }

      if (!(await this.validateForm())) {
        this.showError('Please fix the highlighted fields before saving');
        return;
      }

      // NOTE: the "confirm before publishing to a live (Active) version" gate is
      // temporarily removed while diagnosing the save issue; will be restored as a
      // styled dialog once saving is confirmed working end-to-end.
      await this.performSave();
    },

    async performSave() {
      this.saving = true;
      try {
        const request = {
          consentVersionID: this.meta.consentVersionID,
          versionCode: this.meta.versionCode,
          description: this.meta.description,
          effectiveStartDate: this.meta.effectiveStartDate,
          effectiveEndDate: this.meta.effectiveEndDate,
          isActive: this.meta.isActive,
          config: this.buildConfigForSave(),
        };

        const response = await this.callApi('UpdateTaxConsentVersion', this.meta.consentVersionID, request);

        if (response.status === 0) {
          this.showSuccess('Saved consent configuration');
          if (response.data) {
            this.meta.updateDate = response.data.updateDate || this.meta.updateDate;
            if (response.data.config) this.config = this.normalizeConfig(response.data.config);
          }
          this.$nextTick(() => {
            this.baseline = this.snapshot();
          });
          await this.loadLogs();
        } else {
          this.showError(response.message || 'Failed to save consent configuration');
        }
      } catch (error) {
        console.error('Error saving consent configuration:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to save consent configuration'));
      } finally {
        this.saving = false;
      }
    },

    onBeforeUnload(event) {
      if (this.isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    },

    goBack() {
      this.$router.push('/TaxConsent');
    },

    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.tc-edit-container {
  padding-bottom: 88px; /* room for the sticky action bar */
}

.cfg-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.85rem;
  color: #6b7280;
  padding: 12px 4px 4px 4px;
}

.cfg-breadcrumb-sep {
  color: #c4c4c4;
}

.cfg-breadcrumb-link {
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  text-decoration: none;
}

.cfg-breadcrumb-link:hover {
  text-decoration: underline;
}

.cfg-breadcrumb-current {
  color: #374151;
  font-weight: 600;
}

.card-section-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  padding-bottom: 4px;
}

.card-section-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  padding: 0 16px 4px 16px;
}

.ml-group {
  margin-top: 16px;
}

.ml-group-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

/* Language-tab status: distinct icon SHAPE per state (not color alone) so it
   reads without relying on color; both carry a text aria-label/title. */
.tc-dot-partial {
  color: #d97706; /* amber-600 — AA for graphical objects */
  vertical-align: super;
}

.tc-dot-empty {
  color: #9ca3af;
  vertical-align: super;
}

/* HTML source editor */
.tc-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 4px 4px;
  background: rgba(0, 0, 0, 0.015);
  height: 28px;
}

.tc-tool-btn {
  min-width: 30px !important;
  padding: 0 6px !important;
  color: #4b5563;
}

.tc-color-picker-wrapper {
  position: relative;
  height: 100%;
  border-radius: 6px;
  border: 1.5px solid rgba(0, 0, 0, 0.12);
  cursor: pointer;
  flex-shrink: 0;
  aspect-ratio: 1 / 1;
  margin: 0px 6px;
}

.tc-color-picker {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.tc-apply-color-btn {
  min-width: 26px !important;
}

.tc-apply-color-letter {
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
}

.tc-source-label {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 4px;
}

.tc-html-source {
  width: 100%;
  min-height: 320px;
  resize: vertical;
  padding: 12px 14px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 8px;
  font-family: 'Fira Code', 'JetBrains Mono', 'Monaco', 'Menlo', monospace;
  font-size: 0.82rem;
  line-height: 1.6;
  color: #1f2937;
  background: #fbfbfd;
  tab-size: 2;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.tc-html-source:hover {
  border-color: rgba(0, 0, 0, 0.45);
}

.tc-html-source:focus {
  outline: none;
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
}

.tc-source-hint {
  font-size: 0.72rem;
  color: #6b7280; /* AA on white for body-size text */
  margin: 6px 2px 0 2px;
}

/* Live preview — a phone mockup of the customer-facing consent screen,
   rendered on the supplied gradient wallpaper. */
.tc-phone {
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 600px;
  margin: 0 auto;
  border-radius: 30px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  color: #1f2937;
}

/* Status bar */
.tc-phone-statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 22px 2px 22px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2937;
}

.tc-phone-signal {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

/* Top bar: circular back button + header title */
.tc-phone-topbar {
  display: flex;
  align-items: center;
  padding: 6px 14px 10px 14px;
}

.tc-phone-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  color: #374151;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.tc-phone-header-title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
  padding-right: 32px; /* balance the back button so the title stays centered */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Frosted consent sheet */
.tc-phone-sheet {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin: 2px 12px 14px 12px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.tc-phone-scroll {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: hidden;
  padding: 18px 18px 4px;
}

.tc-preview-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Avatar / icon */
.tc-phone-avatar {
  width: 72px;
  height: 72px;
  margin: 0 auto 10px auto;
  border-radius: 50%;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.tc-phone-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tc-phone-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  text-align: center;
  margin-bottom: 2px;
}

.tc-phone-subtitle {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin-bottom: 12px;
}

.tc-icon-thumb {
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* Action buttons pinned at the bottom of the sheet */
.tc-phone-actions {
  display: flex;
  gap: 10px;
  padding: 12px 16px 16px 16px;
}

.tc-phone-btn {
  flex: 1 1 0;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 11px 0;
  border-radius: 999px;
}

.tc-phone-btn.ghost {
  color: #4b5563;
  background: rgba(255, 255, 255, 0);
  border: 1px solid rgba(42, 49, 77);
}

.tc-phone-btn.primary {
  color: #fff;
  background: rgba(42, 49, 77);
  box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.35);
}

/* The toolbar swatch sizes itself off .tc-toolbar's fixed height; inside a
   text field it needs explicit dimensions or it collapses to nothing. */
.tc-cancel-color-swatch {
  width: 26px;
  height: 26px;
  margin: 0 8px 0 0;
  align-self: center;
}

/* Cancel-consent mode: a single centered link instead of the two buttons */
.tc-phone-actions-cancel {
  justify-content: center;
}

.tc-phone-cancel-link {
  text-align: center;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 11px 0;
  line-height: 1.35;
}

/* Body HTML content (shared sanitized render of the consent body) */

.tc-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 130px;
  color: #6b7280;
  font-size: 0.82rem;
  font-style: italic;
  text-align: center;
}

.tc-preview-approx {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  color: #6b7280;
  margin: 8px 2px 0 2px;
}

.tc-preview-body :deep(p) {
  margin: 0 0 0.7em 0;
}
.tc-preview-body :deep(ul),
.tc-preview-body :deep(ol) {
  margin: 0.2em 0 0.8em 0;
  padding-left: 1.3em;
  text-align: left;
}
.tc-preview-body :deep(li) {
  margin-bottom: 0.45em;
}
.tc-preview-body :deep(h1),
.tc-preview-body :deep(h2),
.tc-preview-body :deep(h3),
.tc-preview-body :deep(h4) {
  font-weight: 700;
  color: #1f2937;
  margin: 1em 0 0.4em;
  line-height: 1.3;
  text-align: center;
}
.tc-preview-body :deep(h4) {
  font-size: 0.92rem;
}
.tc-preview-body :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
.tc-preview-body :deep(strong) {
  font-weight: 700;
  color: #111827;
}
.tc-preview-body :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5em 0;
}
.tc-preview-body :deep(th),
.tc-preview-body :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 6px 10px;
  text-align: left;
}

.tc-action-bar {
  position: sticky;
  bottom: 0;
  z-index: 4;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
  padding: 10px 0;
}

.scroll-area {
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  padding-right: 8px;
  height: 10;
}

.scroll-area::-webkit-scrollbar {
  width: 8px;
}

.scroll-area::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scroll-area::-webkit-scrollbar-thumb {
  background: #54a6ab;
  border-radius: 4px;
}

.scroll-area::-webkit-scrollbar-thumb:hover {
  background: #888;
}
</style>
